import { useOs } from '@mantine/hooks';
import { useRef } from 'react';
import {
	BOTTOM_PADDING,
	FOOTER_HEIGHT,
	HEADER_HEIGHT,
	ROW_HEIGHT,
} from './TableV2.styles';

export function useTableV2Height({
	totalCountWithNesting,
	paginationSize,
	height,
	extraFooterSpace,
}: {
	totalCountWithNesting: number;
	paginationSize: number;
	height: number | string | undefined;
	extraFooterSpace: boolean;
}) {
	const tableRef = useRef<HTMLTableSectionElement>(null);

	// Store initial distance in a ref so it persists without re-renders
	const initialDistanceRef = useRef<number | null>(null);

	// Only calculate the distance once
	if (initialDistanceRef.current === null && tableRef.current) {
		initialDistanceRef.current = tableRef.current.getBoundingClientRect().top;
	}

	// Use initial distance or fallback to 0
	const distanceFromTop = initialDistanceRef.current ?? 0;

	const renderedHeight = tableRef.current?.offsetHeight;

	let tableMaxHeight =
		totalCountWithNesting > 0
			? HEADER_HEIGHT +
				2 +
				Math.min(paginationSize, totalCountWithNesting) * ROW_HEIGHT +
				// Account for the border widths of each row.
				Math.min(paginationSize, totalCountWithNesting) +
				1 +
				(extraFooterSpace ? FOOTER_HEIGHT : 0)
			: undefined;

	const os = useOs();
	if (!!tableMaxHeight && os === 'windows') {
		// We need to add a 20px gutter to the max-height on Windows to account for
		// the scrollbars taking over the element's space in this issue we see that
		// the horizontal scrollbar is forcing the vertical scrollbar to appear,
		// which is causing the scrollbar to hide part of the elements on Mac this
		// doesn't happen because Mac uses overlay scrollbars, while Windows uses
		// classic scrollbars
		// Reference:
		// https://developer.mozilla.org/en-US/docs/Web/CSS/scrollbar-gutter Note:
		// scrollbar-gutter doesn't work either because all of these elements have
		// fixed height. We need to revisit this whole structure to fix this
		// properly. This will cause some unwanted extra space at the bottom for
		// some windows users if the browser decides to use overlay scrollbars. This
		// is less of a problem than the current issue.
		tableMaxHeight += 20;
	}

	let tableHeight: number | string | undefined = height;
	if (!tableHeight) {
		// If distanceFromTop is unreasonably large (resulting in negative or tiny viewport space)
		// or if the rendered height is unusually small during loading, default to 80vh
		if (
			distanceFromTop > window.innerHeight - 200 ||
			(renderedHeight && renderedHeight < 200)
		) {
			tableHeight = '80vh';
		} else {
			tableHeight = `calc(100vh - ${distanceFromTop + BOTTOM_PADDING}px)`;
		}
	}

	return {
		tableRef,
		tableHeight,
		tableMaxHeight,
	};
}
